/**
 * Phase 3H Part B — HQ Change Order modal.
 *
 * Opened from the Client Profile → Agreements tab for any ACCEPTED
 * PROJECT agreement. Pre-fills canonical data (customer, original
 * reference, currency, project value, paid-so-far). Operator adds
 * scope changes + a revised total; if they choose to revise unpaid
 * instalments, the modal validates the revised sum reconciles.
 *
 * On confirm calls the existing canonical endpoint:
 *   POST /api/hq/control/agreements/change-orders
 * No parallel change-order system is introduced.
 */
import React, { useEffect, useMemo, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { toast } from "sonner";
import { X, Loader2, Plus, Trash2, ShieldCheck } from "lucide-react";

const CUR_SYM = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const money = (m, c) => `${CUR_SYM[c] || c + " "}${(Number(m || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PAYMENT_IMPACTS = [
  { key: "no_change",   label: "No immediate payment (value stays as remaining)" },
  { key: "revise_unpaid", label: "Revise unpaid future instalments" },
];

export default function CreateChangeOrderModal({ open, onClose, agreement, invoice, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [addedScope, setAddedScope] = useState([""]);
  const [removedScope, setRemovedScope] = useState([""]);
  const [valueDelta, setValueDelta] = useState("0.00");  // decimal, may be negative
  const [impact, setImpact] = useState("no_change");
  const [revisedInsts, setRevisedInsts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const currency = invoice?.currency || agreement?.currency || "GBP";
  const originalTotal = Number(invoice?.total_amount_minor || agreement?.project_snapshot?.project_value_minor || 0);
  const paidMinor = Number(invoice?.amount_paid_minor || 0);
  const deltaMinor = Math.round(parseFloat(valueDelta || "0") * 100);
  const revisedTotal = originalTotal + deltaMinor;
  const revisedRemaining = Math.max(revisedTotal - paidMinor, 0);

  useEffect(() => {
    if (!open) return;
    setTitle(""); setDescription(""); setAddedScope([""]); setRemovedScope([""]);
    setValueDelta("0.00"); setImpact("no_change"); setResult(null);
    // Seed a revised-unpaid schedule from the current unpaid instalments.
    const unpaid = (invoice?.installments || []).filter((i) => i.status !== "PAID");
    setRevisedInsts(unpaid.length ? unpaid.map((i) => ({
      label: i.label, amount: (Number(i.amount_minor) / 100).toFixed(2), due_date: i.due_date || "",
    })) : [{ label: "Payment 1", amount: (revisedRemaining / 100).toFixed(2), due_date: "" }]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice]);

  const revisedSum = revisedInsts.reduce((a, i) => a + Math.round(parseFloat(i.amount || "0") * 100), 0);
  const scheduleValid = impact === "no_change" || revisedSum === revisedRemaining;
  const canSubmit = !saving && description.trim().length > 0
    && revisedTotal >= paidMinor && scheduleValid;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        invoice_id: invoice.id,
        description: `${title ? title + " — " : ""}${description}`,
        added_scope:   addedScope.map((s) => s.trim()).filter(Boolean),
        removed_scope: removedScope.map((s) => s.trim()).filter(Boolean),
        revised_total_minor: revisedTotal,
        revised_installments: impact === "revise_unpaid" ? revisedInsts.map((i) => ({
          label: i.label, amount_minor: Math.round(parseFloat(i.amount || "0") * 100),
          due_date: i.due_date || null,
        })) : [],
      };
      const r = await hqApi.post("/control/agreements/change-orders", payload);
      setResult(r.data);
      toast.success(`Change order ${r.data.change_order_reference} created`);
      onCreated?.(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "object" ? (d.reason || d.code || "Failed to create") : (d || "Failed to create");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="hq-change-order-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Change Order</div>
            <h2 className="font-black text-lg mt-1">
              {result ? "Change order created" : `Amend ${agreement?.agreement_reference || ""}`}
            </h2>
          </div>
          <button data-testid="hq-change-order-close" onClick={onClose} className="p-2 rounded-full hover:bg-[#F6F5F2]"><X size={16}/></button>
        </div>

        {!result ? (
          <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto text-sm">
            {/* Canonical prefilled data — read-only */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 rounded-xl bg-[#FDFCF9] border border-[#EAEAEA] p-3">
              <Cell label="Original ref" value={agreement?.agreement_reference}/>
              <Cell label="Service" value={(agreement?.service_type || "").replace(/_/g, " ")}/>
              <Cell label="Currency" value={currency}/>
              <Cell label="Original value" value={money(originalTotal, currency)}/>
              <Cell label="Amount paid" value={money(paidMinor, currency)}/>
              <Cell label="Outstanding" value={money(originalTotal - paidMinor, currency)}/>
              <Cell label="Schedule" value={agreement?.payment_schedule?.kind || "—"}/>
              <Cell label="Status" value={agreement?.status}/>
            </div>

            <Field label="Change order title">
              <input data-testid="hq-change-order-title" value={title} onChange={(e) => setTitle(e.target.value)}
                     placeholder="e.g. Additional reporting module"
                     className="w-full px-3 py-2 rounded-lg border font-black"/>
            </Field>
            <Field label="Change description (required)">
              <textarea data-testid="hq-change-order-description" rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
                         placeholder="Describe the change — what is being added, removed, or altered."
                         className="w-full px-3 py-2 rounded-lg border"/>
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ListInput label="Added scope" testId="hq-change-order-added" items={addedScope} setItems={setAddedScope}/>
              <ListInput label="Removed scope" testId="hq-change-order-removed" items={removedScope} setItems={setRemovedScope}/>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label={`Value change (${currency})`}>
                <input data-testid="hq-change-order-delta" inputMode="decimal" value={valueDelta}
                       onChange={(e) => setValueDelta(e.target.value)}
                       placeholder="0.00" className="w-full px-3 py-2 rounded-lg border font-black"/>
                <div className="text-[10px] mt-1 text-[#8A8A8A]">Positive to add, negative to reduce.</div>
              </Field>
              <Totem label="Revised project value">
                <span data-testid="hq-change-order-revised-total">{money(revisedTotal, currency)}</span>
              </Totem>
              <Totem label="Revised remaining" tone={revisedTotal < paidMinor ? "red" : "green"}>
                <span data-testid="hq-change-order-revised-remaining">{money(revisedRemaining, currency)}</span>
              </Totem>
            </div>

            {revisedTotal < paidMinor && (
              <div className="p-3 rounded-lg bg-[#FDEAEA] border border-[#F5CAC3] text-[12px] text-[#C1350B]"
                   data-testid="hq-change-order-below-paid">
                Revised total ({money(revisedTotal, currency)}) cannot be less than the amount already paid ({money(paidMinor, currency)}).
              </div>
            )}

            <Field label="Payment impact">
              <div className="flex flex-wrap gap-2">
                {PAYMENT_IMPACTS.map((p) => (
                  <button key={p.key} type="button"
                          data-testid={`hq-change-order-impact-${p.key}`}
                          onClick={() => setImpact(p.key)}
                          className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.14em] uppercase border ${impact === p.key ? "bg-black text-white border-black" : "bg-white border-[#EAEAEA]"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </Field>

            {impact === "revise_unpaid" && (
              <Field label={`Revised unpaid instalments (must sum to ${money(revisedRemaining, currency)})`}>
                <div className="space-y-2">
                  {revisedInsts.map((r, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`hq-change-order-row-${idx}`}>
                      <input placeholder="Label" value={r.label}
                             onChange={(e) => setRevisedInsts(revisedInsts.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))}
                             className="col-span-4 px-3 py-2 rounded-lg border"/>
                      <input placeholder="0.00" inputMode="decimal" value={r.amount}
                             onChange={(e) => setRevisedInsts(revisedInsts.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))}
                             className="col-span-3 px-3 py-2 rounded-lg border font-black"/>
                      <input type="date" value={r.due_date}
                             onChange={(e) => setRevisedInsts(revisedInsts.map((x, i) => i === idx ? { ...x, due_date: e.target.value } : x))}
                             className="col-span-4 px-3 py-2 rounded-lg border"/>
                      <button type="button" onClick={() => setRevisedInsts(revisedInsts.filter((_, i) => i !== idx))}
                              disabled={revisedInsts.length === 1}
                              className="col-span-1 h-10 rounded-lg border hover:border-[#C1350B] disabled:opacity-30 flex items-center justify-center">
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setRevisedInsts([...revisedInsts, { label: `Payment ${revisedInsts.length + 1}`, amount: "0.00", due_date: "" }])}
                          className="inline-flex items-center gap-1 text-[11px] font-black tracking-[0.14em] uppercase text-[#FF6A00]">
                    <Plus size={12}/> Add instalment
                  </button>
                </div>
                <div className="mt-2 text-[11px] text-[#8A8A8A]">
                  Scheduled sum: <strong className={revisedSum === revisedRemaining ? "text-[#0FA958]" : "text-[#C1350B]"}
                                          data-testid="hq-change-order-scheduled-sum">
                    {money(revisedSum, currency)}
                  </strong> · Required: {money(revisedRemaining, currency)}
                </div>
              </Field>
            )}
          </div>
        ) : (
          <div className="p-6 space-y-4" data-testid="hq-change-order-success">
            <div className="rounded-xl bg-[#E7F7EE] border border-[#B7E4C7] p-4">
              <div className="flex items-center gap-2 text-[#0FA958]">
                <ShieldCheck size={16}/>
                <div className="text-[10px] tracking-[0.24em] uppercase font-black">Change order issued</div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Cell label="Change order ref" value={result.change_order_reference}/>
                <Cell label="Supersedes" value={result.supersedes_reference}/>
                <Cell label="Revised total" value={money(revisedTotal, currency)}/>
                <Cell label="Status" value="ISSUED · awaiting acceptance"/>
              </div>
              <div className="mt-3 text-[12px] text-[#4A4A4A]">
                The customer must accept the change order via the shared review page before further instalments can be paid.
              </div>
              <a href={`/agreement/${result.token}`} target="_blank" rel="noreferrer"
                 data-testid="hq-change-order-review-link"
                 className="mt-3 inline-flex items-center gap-1 text-[11px] font-black tracking-[0.14em] uppercase text-[#FF6A00] hover:underline">
                Preview customer review page →
              </a>
            </div>
          </div>
        )}

        <div className="px-6 py-4 border-t border-[#EAEAEA] flex items-center justify-end gap-2">
          {!result && (
            <>
              <button data-testid="hq-change-order-cancel" onClick={onClose} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase">
                Cancel
              </button>
              <button data-testid="hq-change-order-submit" onClick={submit} disabled={!canSubmit}
                      className={`px-4 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase ${canSubmit ? "bg-[#FF6A00] text-black hover:bg-[#e75f00]" : "bg-[#F6F5F2] text-[#8A8A8A] cursor-not-allowed"}`}>
                {saving ? <Loader2 size={12} className="inline animate-spin mr-1"/> : null}
                Create Change Order
              </button>
            </>
          )}
          {result && (
            <button data-testid="hq-change-order-done" onClick={onClose} className="px-4 py-2 rounded-full bg-black text-white text-[11px] font-black tracking-[0.16em] uppercase">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">{label}</div>
      {children}
    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="text-[9px] tracking-[0.18em] uppercase font-bold text-[#8A8A8A]">{label}</div>
      <div className="font-bold break-words">{value || "—"}</div>
    </div>
  );
}

function Totem({ label, tone, children }) {
  const cls = tone === "green" ? "text-[#0FA958]" : tone === "red" ? "text-[#C1350B]" : "text-black";
  return (
    <div className="rounded-lg border border-[#EAEAEA] px-3 py-2 text-center">
      <div className="text-[9px] tracking-[0.18em] uppercase font-bold text-[#8A8A8A]">{label}</div>
      <div className={`mt-1 font-black text-base ${cls}`}>{children}</div>
    </div>
  );
}

function ListInput({ label, items, setItems, testId }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">{label}</div>
      <div className="space-y-1">
        {items.map((v, idx) => (
          <div key={idx} className="flex gap-1">
            <input data-testid={`${testId}-${idx}`} value={v}
                   onChange={(e) => setItems(items.map((x, i) => i === idx ? e.target.value : x))}
                   className="flex-1 px-3 py-1.5 rounded-lg border text-[12px]"/>
            <button type="button" onClick={() => setItems(items.length === 1 ? [""] : items.filter((_, i) => i !== idx))}
                    className="h-8 w-8 rounded-lg border flex items-center justify-center hover:border-[#C1350B]">
              <Trash2 size={12}/>
            </button>
          </div>
        ))}
        <button type="button" onClick={() => setItems([...items, ""])}
                className="inline-flex items-center gap-1 text-[10px] font-black tracking-[0.14em] uppercase text-[#FF6A00]">
          <Plus size={10}/> Add
        </button>
      </div>
    </div>
  );
}
